import axios, { isAxiosError } from 'axios';
import { Location, NavigateFunction } from 'react-router-dom';

import { BOOKING_ENTITY_URL, CANCELLATION_REASON_API_URL, FERRY_BOOKING_URL, SERVER_URL } from 'Constants';
import { dispatchResetReservationEvent } from 'Events/HumanWritten/ResetReservationEvent';
import { PAYMENT_REQUEST_TIMEOUT_MS } from 'Services/Api/_HumanWritten/PaymentService';
import useAsync from 'Hooks/useAsync';
import { AlterationExpirationDto, BulkFerryBookingCreationDto, FerryBookingCreationDto } from 'Models/Dtos/BookingDtos';
import { appConfigStore } from 'Models/AppConfigStore';
import { BookingEntity } from 'Models/Entities';
import { feeType } from 'Models/Enums';
import { handleForbiddenRequest } from 'Modules/Auth/authUtils';
import { getFerryBookingTransactionIdFromStorage } from 'Services/Api/_HumanWritten/BookingService/BookingService';
import { buildUrl } from 'Util/FetchUtils';
import { stringNotEmpty } from 'Util/TypeGuards';
import {
	BookingCancellationDto,
	BookingCancellationInformationDto,
	RefundCardDto,
} from 'Views/Components/_HumanWritten/Bookings/BookingDetailedView/Modals/CancelBooking/NewCancelBookingModal';
import { BookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BaseBookingWizardData';
import { PassengerDetail } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/PassengerDetail';
import { serialisePassengerDOB } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Passengers/PassengerTabHelpers';
import { FreightBookingDetail } from 'Views/Components/_HumanWritten/CheckIn/FerryCheckIn/context/BookingFormState';
import { Colors } from 'Views/Components/Button/Button';
import { newAlertModal } from 'Views/Components/Modal/NewAlertModal';

/**
 * Takes data and turns it into an instance of the booking creation dto. This function should take a BookingWizardData,
 * but as this function needs to be called from within a generic component, we can't guarantee that the type will be
 * correct, so we had to accept anything and then provide default values if the fields we need aren't present
 */
export function dataToFerryTripBookingDto(
	data: BookingWizardData,
	bypassSpaceCheck: boolean,
	inCheckIn: boolean,
	useBookingForPriceCalculation: boolean = false,
): FerryBookingCreationDto {
	const oneWay: boolean = data.tripType === 'one way';

	const bookingInformation = {
		inCheckIn: inCheckIn,
		tabSelected: data.tabSelected,
		type: data.innerWizardData.type,
		wizardMode: data.wizardMode,

		// Feature: Multi-stop - save booking
		departingJourney: data.departingJourney,
		returningJourney: data.returningJourney,

		userId: stringNotEmpty(data.userId) ? data.userId : undefined,
		bookingToEdit: data.bookingToEdit !== '' && data.bookingToEdit !== undefined ? data.bookingToEdit : undefined,

		// The server is expecting to see a trip id here, even though it is not used during bulk booking. We don't allow
		// progression to the booking wizard for bulk booking, and if the last one is removed we quit out, so we can
		// assume that there is at least one id here
		departureTicketId: stringNotEmpty(data.departureTicketId) ? data.departureTicketId : undefined,

		// This will only be set for return trips, so we need to handle the situation where the field isn't set
		// eslint-disable-next-line no-nested-ternary
		returningTicketId: oneWay
			? undefined
			: stringNotEmpty(data.returningTicketId)
				? data.returningTicketId
				: undefined,

		// Required by the server
		location: 'BOOKING_WIZARD',
		existingTransactionId: getFerryBookingTransactionIdFromStorage() ?? undefined,
		bypassSpaceCheck: bypassSpaceCheck,
		useBookingForPriceCalculation: useBookingForPriceCalculation,
		bulkBookingFilters: data.bulkBookingFilters,
	} as FerryBookingCreationDto;

	if (data.innerWizardData.type === 'passenger' || data.innerWizardData.type === 'vehicle') {
		/*
			Only a vehicle booking carries a driver, and this is where that is enforced.

			The wizard deliberately keeps the flag in memory when the user switches to walk-on, so
			that switching back restores the booking's original driver rather than handing the role
			to whichever eligible passenger type happens to come first. The cost of that is a
			passenger-only payload that could still be carrying one, so it is dropped here.

			`serialisePassengerDOB` already returns copies, so stripping its output never reaches
			back into the wizard's own tickets — which is the whole point of keeping them.
		*/
		const isVehicleBooking = data.innerWizardData.type === 'vehicle';
		const passengerTickets = (tickets: PassengerDetail[] | undefined) =>
			serialisePassengerDOB(tickets).map(ticket =>
				isVehicleBooking ? ticket : { ...ticket, driver: false },
			);

		bookingInformation.adultTickets = passengerTickets(data.innerWizardData.adultTickets);
		bookingInformation.childTickets = passengerTickets(data.innerWizardData.childTickets);
		bookingInformation.infantTickets = passengerTickets(data.innerWizardData.infantTickets);
		bookingInformation.passengerDTickets = passengerTickets(data.innerWizardData.passengerDTickets);
		bookingInformation.passengerETickets = passengerTickets(data.innerWizardData.passengerETickets);
		bookingInformation.passengerFTickets = passengerTickets(data.innerWizardData.passengerFTickets);
		bookingInformation.passengerGTickets = passengerTickets(data.innerWizardData.passengerGTickets);
		bookingInformation.passengerHTickets = passengerTickets(data.innerWizardData.passengerHTickets);

		bookingInformation.departingTripOptions = data.innerWizardData.departingTripOptions ?? null;
		bookingInformation.returningTripOptions = oneWay ? [] : (data.innerWizardData.returningTripOptions ?? null);

		bookingInformation.note = data.innerWizardData.note ?? undefined;

		// Admin-configured dynamic field values flow through to AlterationContext on save.
		bookingInformation.dynamicFieldValues = data.innerWizardData.dynamicFieldValues ?? undefined;
	}

	// Primary booking contact name — only when the operator collects one. Otherwise nothing is sent:
	// `primaryFirstName` is still set from the selected customer and from the booking summary, which
	// falls back to the account holder, and the server writes whatever arrives onto a passenger.
	// That is how a corporate customer's name replaced the passenger's on every edit (#2834).
	if (data.innerWizardData.type === 'passenger' && appConfigStore.primaryBookingNameEnabled) {
		// The serialised copies, so the flag set below is the one the server receives.
		const tickets = [
			...(bookingInformation.adultTickets ?? []),
			...(bookingInformation.childTickets ?? []),
			...(bookingInformation.infantTickets ?? []),
			...(bookingInformation.passengerDTickets ?? []),
			...(bookingInformation.passengerETickets ?? []),
			...(bookingInformation.passengerFTickets ?? []),
			...(bookingInformation.passengerGTickets ?? []),
			...(bookingInformation.passengerHTickets ?? []),
		];
		// Chosen here and flagged explicitly — the passenger already flagged, else the first adult,
		// else the first passenger, the same order the server falls back to. The server only replaces
		// the name of a passenger the payload flagged; one it has to pick for itself keeps its name.
		// Without the flag, renaming the booking on a booking whose flag had been lost would not stick.
		const primaryContactPassenger =
			tickets.find(passenger => passenger.isPrimaryContact) ?? bookingInformation.adultTickets?.[0] ?? tickets[0];
		tickets.forEach(passenger => {
			passenger.isPrimaryContact = passenger === primaryContactPassenger;
		});

		// When passenger info is collected, the primary-contact passenger's name is the
		// editing surface, so it wins. Otherwise the BookingDetails tab owns the name and
		// must take precedence — alterations seed the passenger tickets with the PREVIOUS
		// booking's primary contact, which would shadow the newly edited value.
		// Blank counts as absent either way: a blank passenger name must not hide the tab's value.
		const passengerInfoCollected = appConfigStore.passengerInfoEnabled;
		const pick = (passengerValue?: string, tabValue?: string) => {
			const [first, second] = passengerInfoCollected ? [passengerValue, tabValue] : [tabValue, passengerValue];
			if (stringNotEmpty(first?.trim())) return first;
			return stringNotEmpty(second?.trim()) ? second : undefined;
		};
		bookingInformation.primaryFirstName = pick(
			primaryContactPassenger?.firstName,
			data.innerWizardData.primaryFirstName,
		);
		bookingInformation.primaryLastName = pick(
			primaryContactPassenger?.lastName,
			data.innerWizardData.primaryLastName,
		);
	}

	if (data.innerWizardData.type === 'vehicle') {
		bookingInformation.driverFirstName = data.innerWizardData.driverFirstName;
		bookingInformation.driverLastName = data.innerWizardData.driverLastName;
		bookingInformation.driverPhone = data.innerWizardData.driverPhone;

		bookingInformation.cargoCharacteristics = data.innerWizardData.cargoCharacteristics;
		bookingInformation.removedInheritedCharacteristics = data.innerWizardData.removedInheritedCharacteristics;
		bookingInformation.cargoIdentification = data.innerWizardData.cargoIdentification;
		bookingInformation.hiredVehicle = data.innerWizardData.hiredVehicle;
		bookingInformation.cargoTypeId = stringNotEmpty(data.innerWizardData.cargoTypeId)
			? data.innerWizardData.cargoTypeId
			: undefined;
		bookingInformation.cargoMake = data.innerWizardData.cargoMake;
		bookingInformation.cargoModel = data.innerWizardData.cargoModel;
		bookingInformation.vehicleLengthId = stringNotEmpty(data.innerWizardData.vehicleLengthId)
			? data.innerWizardData.vehicleLengthId
			: undefined;
		bookingInformation.vehicleWeightId = stringNotEmpty(data.innerWizardData.vehicleWeightId)
			? data.innerWizardData.vehicleWeightId
			: undefined;
		bookingInformation.vehicleWidthId = stringNotEmpty(data.innerWizardData.vehicleWidthId)
			? data.innerWizardData.vehicleWidthId
			: undefined;

		bookingInformation.trailerLengthId = stringNotEmpty(data.innerWizardData.trailerLengthId)
			? data.innerWizardData.trailerLengthId
			: undefined;
		bookingInformation.trailerWeightId = stringNotEmpty(data.innerWizardData.trailerWeightId)
			? data.innerWizardData.trailerWeightId
			: undefined;
		bookingInformation.trailerTypeId =
			stringNotEmpty(data.innerWizardData.trailerTypeId) && data.innerWizardData.trailerTypeId !== 'NO_TRAILER'
				? data.innerWizardData.trailerTypeId
				: undefined;
		bookingInformation.trailerWidthId = stringNotEmpty(data.innerWizardData.trailerWidthId)
			? data.innerWizardData.trailerWidthId
			: undefined;
		bookingInformation.trailerCargoCharacteristics = data.innerWizardData.trailerCargoCharacteristics;

		bookingInformation.vehicleExactWeight = data.innerWizardData.vehicleExactWeight ?? undefined;
		bookingInformation.vehicleExactWeightUnit = data.innerWizardData.vehicleExactWeightUnit ?? undefined;
		bookingInformation.trailerExactWeight = data.innerWizardData.trailerExactWeight ?? undefined;
		bookingInformation.trailerExactWeightUnit = data.innerWizardData.trailerExactWeightUnit ?? undefined;

		bookingInformation.note = data.innerWizardData.note ?? undefined;
	}

	if (data.innerWizardData.type === 'freight') {
		bookingInformation.freightTypeId = data.innerWizardData.freightTypeId || undefined; // Set as undefined if empty string
		bookingInformation.freightCategoryId = data.innerWizardData.freightCategoryId || undefined;
		bookingInformation.handlingIds = data.innerWizardData.handlingIds;
		bookingInformation.quantity = data.innerWizardData.quantity ?? undefined;
		bookingInformation.weight = data.innerWizardData.weight ?? undefined;
		bookingInformation.width = data.innerWizardData.width ?? undefined;
		bookingInformation.height = data.innerWizardData.height ?? undefined;
		bookingInformation.length = data.innerWizardData.length ?? undefined;
		bookingInformation.description = data.innerWizardData.description;

		bookingInformation.receiverFirstName = data.innerWizardData.receiverFirstName;
		bookingInformation.receiverLastName = data.innerWizardData.receiverLastName;
		bookingInformation.receiverPhone = data.innerWizardData.receiverPhone;
		bookingInformation.receiverEmail = data.innerWizardData.receiverEmail;
		bookingInformation.receiverCompanyName = data.innerWizardData.receiverCompanyName;
		bookingInformation.notifyFirstName = data.innerWizardData.notifyFirstName;
		bookingInformation.notifyLastName = data.innerWizardData.notifyLastName;
		bookingInformation.notifyPhone = data.innerWizardData.notifyPhone;
		bookingInformation.notifyEmail = data.innerWizardData.notifyEmail;
		bookingInformation.notifyCompanyName = data.innerWizardData.notifyCompanyName;

		// Admin-configured dynamic field values flow through to AlterationContext on save.
		bookingInformation.dynamicFieldValues = data.innerWizardData.dynamicFieldValues ?? undefined;

		// Add ons
		bookingInformation.departingTripOptions = data.innerWizardData.departingTripOptions ?? null;

		if (data.innerWizardData.items.length > 0) {
			bookingInformation.bulkFreightWizardData = {
				items: data.innerWizardData.items.map(item => {
					const { quantity, weight, width, height, length } = item;

					if (quantity === null) {
						throw new Error('The field quantity in FreightBookingDetailState cannot be null');
					}
					if (weight === null) {
						throw new Error('The field weight in FreightBookingDetailState cannot be null.');
					}
					if (width === null) {
						throw new Error('The field width in FreightBookingDetailState cannot be null.');
					}
					if (height === null) {
						throw new Error('The field height in FreightBookingDetailState cannot be null.');
					}
					if (length === null) {
						throw new Error('The field length in FreightBookingDetailState cannot be null.');
					}

					const detail: FreightBookingDetail = {
						freightTypeId: item.freightTypeId,
						freightCategoryId: item.freightCategoryId,
						handlingIds: item.handlingIds,
						quantity,
						weight,
						width,
						height,
						length,
						description: item.description,
						addOns: [...item.addOns],
					};

					return detail;
				}),
			};
		}
	}

	return bookingInformation;
}

export function dataToBulkFerryTripBookingDto(
	data: BookingWizardData,
	bypassSpaceCheck: boolean,
	inCheckIn: boolean,
	useBookingForPriceCalculation: boolean = false,
): BulkFerryBookingCreationDto {
	// Re-use logic for base fields, but cast to bulk specific type
	const baseDto = dataToFerryTripBookingDto(data, bypassSpaceCheck, inCheckIn, useBookingForPriceCalculation);

	if (!data.isBulkBookingFlow) {
		throw new Error('Bulk booking data is incomplete');
	}

	return {
		...baseDto,
		bulkBookingFilters: data.bulkBookingFilters!,
		originLocationId: data.fromLocationId,
		destinationLocationId: data.toLocationId,
	};
}

/**
 * The 200 payload of the booking create/alter endpoints (server-side `BookingCreationIds`).
 *
 * These endpoints use `validateStatus: () => true`, so non-2xx responses come back here rather
 * than throwing — and on 400 the payload is an error-message string instead. That is why the
 * response generics below are `BookingCreationIds | string`.
 */
export interface BookingCreationIds {
	bookingId: string;
	transactionId: string;
}

export async function createBulkBooking(bookingInformation: BulkFerryBookingCreationDto, abortSignal?: AbortSignal) {
	return axios.post<BookingCreationIds | string>(`${FERRY_BOOKING_URL}/new-bulk-booking`, bookingInformation, {
		validateStatus: () => true,
		signal: abortSignal,
	});
}

export async function editCartBulkBooking(bookingInformation: BulkFerryBookingCreationDto, abortSignal?: AbortSignal) {
	return axios.post<BookingCreationIds | string>(`${FERRY_BOOKING_URL}/edit-cart-bulk-booking`, bookingInformation, {
		validateStatus: () => true,
		signal: abortSignal,
	});
}

// Create a new booking end-point
export async function createFerryBooking(bookingInformation: FerryBookingCreationDto) {
	return axios.post<BookingCreationIds | string>(`${FERRY_BOOKING_URL}/new-booking`, bookingInformation, {
		validateStatus: () => true,
	});
}

// Alter a booking end-point
export async function alterBooking(bookingInformation: FerryBookingCreationDto) {
	return axios.post<BookingCreationIds | string>(`${FERRY_BOOKING_URL}/alter-booking`, bookingInformation, {
		validateStatus: () => true,
	});
}

// Cancel ferry booking end-point
export interface CancellationReasonOptionDto {
	id: string;
	reason: string;
	reasonKey?: string;
	showMoreInformation: boolean;
	moreInformationRequired: boolean;
	moreInformationLabel?: string;
	moreInformationLabelKey?: string;
	moreInformationPlaceholder?: string;
	moreInformationPlaceholderKey?: string;
}

export interface CancellationReasonOptionsDto {
	/** Whether the wizard must block Confirm until a reason is chosen. The server already accounts for
	 *  there being nothing to choose, so an empty `options` always comes back with `required: false`. */
	required: boolean;
	options: CancellationReasonOptionDto[];
}

/**
 * The reasons offered for this cancellation, already filtered server-side by the caller's role and by
 * the booking's type. Parking passes `bookingType` instead of a booking id, because a parking
 * reservation is not a booking.
 */
export async function getCancellationReasons(args: { bookingId?: string; bookingType?: string }) {
	const response = await axios.get(buildUrl(CANCELLATION_REASON_API_URL, {
		...(args.bookingId ? { bookingId: args.bookingId } : {}),
		...(args.bookingType ? { bookingType: args.bookingType } : {}),
	}));
	return response.data as CancellationReasonOptionsDto;
}

export async function cancelBooking(bookingInfo: BookingCancellationDto) {
	return axios.post(`${FERRY_BOOKING_URL}/cancel-booking`, bookingInfo, {
		validateStatus: () => true,
	});
}

export async function undoCancelBooking(bookingId: string) {
	return axios.post(`${FERRY_BOOKING_URL}/undo-cancel-booking/${bookingId}`);
}

export interface TransferBookingRequest {
	bookingId: string;
	toUserId: string;
	reason: string;
	transferBoth: boolean;
}

export interface TransferBookingResult {
	bookingId: string;
	transferId: string;
	fromUserId: string;
	toUserId: string;
	transferredAt: string;
}

export async function transferBooking(request: TransferBookingRequest) {
	const res = await axios.post<TransferBookingResult>(`${FERRY_BOOKING_URL}/transfer-booking`, request);
	return res.data;
}

export async function getBookingCancellationRules(bookingId: string, cancelReturn: boolean) {
	const results = await axios.get(
		buildUrl(`${FERRY_BOOKING_URL}/cancellation-rules`, {
			bookingId,
			cancelReturn: cancelReturn.toString(),
		}),
	);
	return results.data as BookingCancellationInformationDto;
}

/**
 * Which cards this booking's refund could go back to.
 *
 * Fetched separately from `getBookingCancellationRules` because it is the only part of the quote
 * that calls the payment provider, and those round trips dominated the wait before the cancel modal
 * would open. The wizard only reads the card breakdown on its confirm step, so it has the whole of
 * steps 1 and 2 to arrive.
 *
 * Resolves to an empty list rather than rejecting when the provider cannot be reached — not knowing
 * which card a refund lands on must not stop a cancellation.
 */
export async function getBookingCancellationRefundCards(bookingId: string) {
	const results = await axios.get(
		buildUrl(`${FERRY_BOOKING_URL}/cancellation-refund-cards`, { bookingId }),
	);
	return results.data as RefundCardDto[];
}

/**
 * Update a booking's alterations from RESERVED to EXPIRED RESERVATION.
 */
export async function removeBookingFromCart(
	bookingId: string,
	removeAssociatedBooking: boolean,
	departureBooking: boolean,
) {
	return axios.post(
		`${FERRY_BOOKING_URL}/expired-reservation/${bookingId}?removeAssociatedBooking=${removeAssociatedBooking}&departureBooking=${departureBooking}`,
	);
}

export async function resetReservationTimer(transactionId: string) {
	// No body: the endpoint takes transactionId from the query string.
	//
	// This used to pass `{ validateStatus: () => true }` here, which is axios's *body* parameter, not
	// its config — the third argument is the config. So the "never reject" it was reaching for never
	// applied, and the option was posted as the request payload instead.
	//
	// Rejecting on a non-2xx is what every caller now depends on, so this is deliberately left as the
	// default rather than moved to the config position: a 403 means the hold has already lapsed, and
	// the timer turns that into the session-expired handling. Silently resolving would leave the
	// countdown running against a reservation that no longer exists.
	//
	// Bounded as well: the payment tab holds its spinner until this settles, so an unanswered
	// request used to mean a spinner that never ended.
	await axios.post(
		`${FERRY_BOOKING_URL}/extend-reservation?transactionId=${transactionId}`,
		undefined,
		{ timeout: PAYMENT_REQUEST_TIMEOUT_MS },
	);

	// Only on success — there is nothing to re-read when the extend was refused.
	dispatchResetReservationEvent();
}

/**
 * Returns true if user can navigate straight to the cart. Otherwise, return false.
 *
 * At the moment this is only valid when a user has an existing reserved booking in local storage.
 */
export function canNavigateToCartStep(location: Location): boolean {
	const { pathname } = location;
	const existingTransaction = getFerryBookingTransactionIdFromStorage();

	return existingTransaction !== null && (pathname === '/booking-wizard/cart' || pathname.includes('/t-and-cs'));
}

/**
 * Returns true when user can navigate to the payment step. Otherwise, return false.
 *
 * This will allow a user to have an incomplete booking in the wizard and navigate to the payment step if there is at
 * least one booking in the cart.
 */
export function canNavigateToPaymentStep(location: Location): boolean {
	const { pathname } = location;
	const existingTransaction = getFerryBookingTransactionIdFromStorage();

	return existingTransaction !== null && pathname === '/booking-wizard/payment';
}

/**
 * Returns true when user can navigate to the post payment step. Otherwise, return false.
 *
 * This will allow a user to have an incomplete booking in the wizard and navigate to the post payment step if there is
 * at least one booking in the cart.
 */
export function canNavigateToPostPaymentStep(location: Location): boolean {
	const { pathname } = location;
	const existingTransaction = getFerryBookingTransactionIdFromStorage();

	return existingTransaction !== null && pathname === '/booking-wizard/post-payment';
}

/**
 * Returns the number of alterations that have been discarded as the user cancels an alteration in the front end.
 * @param navigate
 * @param alterationExpiryInfo
 */
export async function discardAlterations(navigate: NavigateFunction, alterationExpiryInfo: AlterationExpirationDto) {
	try {
		const result = await axios.post(
			`${SERVER_URL}/api/entity/AlterationEntity/cancel-alteration`,
			alterationExpiryInfo,
		);
	} catch (e: any) {
		// The body shape is not guaranteed. `BadRequest(e.Message)` on an [ApiController] comes back
		// as a ProblemDetails OBJECT rather than the bare string this used to assume, and a network
		// failure carries no `response` at all. Both made the guard itself throw — `e?.response` was
		// optional-chained but `.data` was not, and `.includes` is not a function on an object.
		//
		// That thrown TypeError escaped `discardAlterations` into its `await` in `onEdit`, which
		// aborted before opening the wizard. The effect was that a booking carrying a stale
		// in-progress alteration (an edit someone backed out of) could not be edited again at all:
		// the dialog closed and nothing happened, with the reason only in the console.
		const errorBody = e?.response?.data;
		const errorText =
			typeof errorBody === 'string'
				? errorBody
				: typeof errorBody?.message === 'string'
					? errorBody.message
					: typeof errorBody?.title === 'string'
						? errorBody.title
						: '';

		if (errorText.includes('Payment already taken')) {
			newAlertModal(
				'Payment already taken',
				'Payment has already been taken for this transaction. Please contact staff if this is an error or start a new transaction.',
				{
					cancelText: 'Ok',
					buttonColour: Colors.Black,
				},
			)
				.then(async () => {
					navigate(`/bookings/${alterationExpiryInfo?.bookingIds[0]}`);
				})
				.catch(async () => {
					navigate(`/bookings/${alterationExpiryInfo?.bookingIds[0]}`);
				});
		}
		console.log('Server error', e);
	}
	return -1;
}

// Remove a towOn removal fee from alteration
export async function removeFee(transactionId: string, feeToRemove: feeType, eventBooking: boolean) {
	return axios
		.post(`${FERRY_BOOKING_URL}/remove-fee/${transactionId}?feeType=${feeToRemove}&eventBooking=${eventBooking}`)
		.then(({ data }) => data);
}

/**
 * Puts the check-in forfeit fee back after a manager removed it. The server re-measures the reduction
 * rather than restoring a remembered figure — the fee has to equal what the fare floor discarded, or the
 * pair stops being cash-neutral. Resolves with the amount restored; 0 means there was nothing to forfeit.
 */
export async function restoreCheckInForfeitFee(transactionId: string): Promise<number> {
	return axios
		.post(`${FERRY_BOOKING_URL}/restore-check-in-forfeit-fee/${transactionId}`)
		.then(({ data }) => Number(data) || 0);
}

// Adds a fee to a transaction
export async function addFeeToTransaction(transactionId: string, feeOptionId: string, forCheckIn: boolean) {
	return axios
		.post(
			`${FERRY_BOOKING_URL}/add-fee-to-transaction/${transactionId}?feeOptionId=${feeOptionId}&forCheckIn=${forCheckIn}`,
		)
		.then(({ data }) => data);
}

export async function removeFeeFromTransaction(
	transactionId: string,
	feeOptionId: string,
	forCheckIn: boolean,
	eventBooking: boolean,
) {
	return axios
		.post(
			`${FERRY_BOOKING_URL}/remove-fee-from-transaction/${transactionId}?feeId=${feeOptionId}&forCheckIn=${forCheckIn}&eventBooking=${eventBooking}`,
		)
		.then(({ data }) => data);
}

export async function fetchBookingForBookingDetailedView(id: string, userId: string | null): Promise<BookingEntity> {
	let bookingId = null;
	let humanReadableId = null;

	// GUID
	if (id.includes('-')) {
		bookingId = id;
	} else {
		// Human readable id
		humanReadableId = id;
	}

	const results = await axios.get(
		buildUrl(`${BOOKING_ENTITY_URL}/fetch-booked-booking-by-id`, {
			bookingId: bookingId ?? '',
			humanReadableId: humanReadableId ?? '',
			userId: userId ?? '',
		}),
	);

	if (!results.data || results.data.length === 0 || !results.data[0]) {
		throw new Error('Booking not found');
	}

	return new BookingEntity(results.data[0]);
}

export interface CancelledBookingSummaryDto {
	id: string;
	humanReadableId?: string;
	startStopId?: string;
	endStopId?: string;
	departureDateTime?: string;
	eventName?: string;
	isMigrated: boolean;
}

/**
 * Lightweight fetch of the bookings cancelled within a transaction (departure + return etc.),
 * used by the cancellation success page. Returns only the fields needed to render each
 * cancelled-trip row — it does not hydrate full booking objects.
 */
export async function fetchCancelledBookingsForTransaction(
	transactionId: string,
): Promise<CancelledBookingSummaryDto[]> {
	const results = await axios.get(
		buildUrl(`${BOOKING_ENTITY_URL}/cancelled-bookings-for-transaction`, {
			transactionId,
		}),
	);
	return results.data ?? [];
}

export async function fetchBookingForBookingDetailedViewByQrCodeId(
	qrCodeId: string,
	userId: string | null,
): Promise<BookingEntity> {
	const results = await axios.get(
		buildUrl(`${BOOKING_ENTITY_URL}/fetch-booked-booking-by-qrcode-id`, {
			qrCodeId,
			userId: userId ?? '',
		}),
	);

	if (!results.data || results.data.length === 0 || !results.data[0]) {
		throw new Error('Booking not found');
	}

	return new BookingEntity(results.data[0]);
}

export async function fetchFullBookingById(
	bookingId: string,
	userId: string | null,
	fetchReturnBooking: boolean = false,
): Promise<BookingEntity> {
	const results = await axios.get(
		buildUrl(`${FERRY_BOOKING_URL}/fetch-full-booking-by-id`, {
			bookingId,
			userId: userId ?? '',
			fetchReturnBooking: fetchReturnBooking.toString(),
		}),
	);
	return new BookingEntity(results.data[0]);
}

/**
 * Fetches bookings to be displayed on the cart
 *
 * @param transactionId - The transaction id to fetch the bookings for
 * @param userId - The user who the bookings are for
 */
export async function fetchBookingsForCart(
	transactionId: string | null,
	skipGlobalErrorHandler: boolean = false,
	abortSignal?: AbortSignal,
): Promise<BookingEntity[]> {
	if (transactionId === null || transactionId === '') {
		return [];
	}
	try {
		const result = await axios.get(buildUrl(`${FERRY_BOOKING_URL}/fetch-bookings-for-cart`, { transactionId }), {
			signal: abortSignal,
		});
		return result.data.map((x: Partial<BookingEntity> | undefined) => new BookingEntity(x));
	} catch (e) {
		if (skipGlobalErrorHandler) {
			throw e;
		}
		if (isAxiosError(e)) {
			if (e.response?.status === 403) {
				handleForbiddenRequest();
			}
		}
		console.error(e);
		return [];
	}
}

export async function checkBookingCanBeModified(bookingId: string) {
	try {
		const results = await axios.get(buildUrl(`${FERRY_BOOKING_URL}/check-booking`, { bookingId }));
		return '';
	} catch (e: any) {
		return e?.response?.data ?? 'error';
	}
}

export const fetchCartBookingTypes = async (transactionId: string) => {
	const result = await axios.get<('event' | 'freight' | 'passenger' | 'vehicle')[]>(
		buildUrl(`${FERRY_BOOKING_URL}/cart/booking-types`, { transactionId }),
	);
	return result.data;
};
